// src/apiClient.js
import axios from "axios";
import crypto from "crypto-js";

// 🔧 Environment Variables
const API_GATEWAY_URL = process.env.REACT_APP_BASEURL;
const API_GATEWAY_ACCESS_KEY = process.env.REACT_APP_API_GATEWAY_ACCESS_KEY;
const API_GATEWAY_SECRET_KEY = process.env.REACT_APP_API_GATEWAY_SECRET_KEY;
const API_GATEWAY_KEY = process.env.REACT_APP_API_KEY;

const REGION = "us-east-1";
const SERVICE = "execute-api";

// ---------------------------
// Create reusable Axios instance
// ---------------------------
export const apiClient = axios.create({
  baseURL: API_GATEWAY_URL,
  timeout: 15000,
});

// -------------------------------------
// 🔐 AWS Signature V4 Generator
// -------------------------------------
function generateAwsSignature(
  accessKey,
  secretKey,
  region,
  service,
  method,
  url
) {
  const datetime = new Date().toISOString().replace(/[:-]|\.\d{3}/g, "");
  const date = datetime.substring(0, 8);

  const parsedUrl = new URL(url);
  const host = parsedUrl.host;
  const canonicalUri = parsedUrl.pathname;
  const canonicalQuerystring = parsedUrl.searchParams.toString();

  const canonicalHeaders = `host:${host}\n` + `x-amz-date:${datetime}\n`;
  const signedHeaders = "host;x-amz-date";

  const payloadHash = crypto.SHA256("").toString(crypto.enc.Hex);

  const canonicalRequest = [
    method,
    canonicalUri,
    canonicalQuerystring,
    canonicalHeaders,
    signedHeaders,
    payloadHash,
  ].join("\n");

  const credentialScope = `${date}/${region}/${service}/aws4_request`;

  const stringToSign = [
    "AWS4-HMAC-SHA256",
    datetime,
    credentialScope,
    crypto.SHA256(canonicalRequest).toString(crypto.enc.Hex),
  ].join("\n");

  const kDate = crypto.HmacSHA256(date, "AWS4" + secretKey);
  const kRegion = crypto.HmacSHA256(region, kDate);
  const kService = crypto.HmacSHA256(service, kRegion);
  const kSigning = crypto.HmacSHA256("aws4_request", kService);

  const signature = crypto
    .HmacSHA256(stringToSign, kSigning)
    .toString(crypto.enc.Hex);

  const authorizationHeader = `AWS4-HMAC-SHA256 Credential=${accessKey}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

  return {
    "x-amz-date": datetime,
    Authorization: authorizationHeader,
  };
}

// -------------------------------------
// 🔑 API Token Generator (from AWS Gateway Lambda)
// -------------------------------------
export async function fetchNewTokenAuth() {
  const url = `${API_GATEWAY_URL}/generate-token`;

  try {
    const sigHeaders = generateAwsSignature(
      API_GATEWAY_ACCESS_KEY,
      API_GATEWAY_SECRET_KEY,
      REGION,
      SERVICE,
      "GET",
      url
    );

    const response = await axios.get(url, {
      headers: {
        //Host: new URL(url).host,
        "Content-Type": "application/json",
        "X-Amz-Date": sigHeaders["x-amz-date"],
        Authorization: sigHeaders.Authorization,
        "X-API-KEY": API_GATEWAY_KEY,
      },
      timeout: 15000,
    });

    return response.data?.token || null;
  } catch (error) {
    console.error("❌ Error fetching token:", error);
    return null;
  }
}

// -------------------------------------
// 🔄 Axios Request Interceptor
// Automatically attach Token + API Key
// -------------------------------------
apiClient.interceptors.request.use(async (config) => {
  let token = await fetchNewTokenAuth();

  config.headers = {
    ...config.headers,
    Authorization: token ? `Bearer ${token}` : "",
    "X-API-KEY": API_GATEWAY_KEY,
    "Content-Type": "application/json",
  };

  return config;
});

// -------------------------------------
// 🚨 Axios Response Interceptor (Logging)
// -------------------------------------
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error:", error?.response || error);
    return Promise.reject(error);
  }
);

export default apiClient;
